import React, { useEffect, useMemo, useState, useCallback } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import {
  DocumentTextIcon,
  MagnifyingGlassIcon,
  ClockIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ChevronRightIcon,
  ShieldCheckIcon,
  ArrowDownTrayIcon,
} from '@heroicons/react/24/outline'
import { Button } from '../../components/ui/button'
import { getLocalApiUrl, authFetch } from '../../config/api'
import { stripTtrSeedDemoSuffix } from '../../utils/ttrMemberDisplay'

function Badge({ bg, color, border, children, title }) {
  return (
    <span
      title={title}
      className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold whitespace-nowrap"
      style={{ backgroundColor: bg, color, border: border ? `1px solid ${border}` : undefined }}
    >
      {children}
    </span>
  )
}

const STATUS_CONFIG = {
  draft:                  { label: 'Draft',         bg: '#fef3c7', color: '#92400e', border: '#fde68a' },
  ready_for_submission:   { label: 'Ready',         bg: '#dbeafe', color: '#1e40af', border: '#bfdbfe' },
  filed:                  { label: 'Filed',         bg: '#dcfce7', color: '#166534', border: '#bbf7d0' },
  not_required:           { label: 'Not required',  bg: '#f1f5f9', color: '#475569', border: '#e2e8f0' },
  cancelled:              { label: 'Cancelled',     bg: '#fee2e2', color: '#991b1b', border: '#fecaca' },
}

function StatusBadge({ status }) {
  const cfg = STATUS_CONFIG[status] || { label: status, bg: '#f1f5f9', color: '#475569', border: '#e2e8f0' }
  return <Badge bg={cfg.bg} color={cfg.color} border={cfg.border}>{cfg.label}</Badge>
}

/** Render a countdown relative to `deadline_at`. Amber < 2 days, red if overdue or within 24h. */
function DeadlinePill({ deadlineAt, status }) {
  if (!deadlineAt) return <span className="text-xs text-gray-400">—</span>
  if (status === 'filed' || status === 'not_required' || status === 'cancelled') {
    return <span className="text-xs text-gray-400">—</span>
  }
  const now = Date.now()
  const d = new Date(deadlineAt).getTime()
  if (Number.isNaN(d)) return <span className="text-xs text-gray-400">—</span>
  const diffMs = d - now
  const overdue = diffMs < 0
  const hours = Math.abs(diffMs) / 3_600_000
  const days = Math.floor(hours / 24)
  const hoursRem = Math.floor(hours - days * 24)
  const label = overdue
    ? `Overdue by ${days}d ${hoursRem}h`
    : `${days}d ${hoursRem}h remaining`
  const color = overdue ? '#991b1b' : hours < 24 ? '#991b1b' : hours < 48 ? '#92400e' : '#166534'
  const bg = overdue ? '#fee2e2' : hours < 24 ? '#fee2e2' : hours < 48 ? '#fef3c7' : '#dcfce7'
  return (
    <span
      className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-mono font-medium"
      style={{ backgroundColor: bg, color }}
    >
      <ClockIcon className="w-3 h-3" />
      {label}
    </span>
  )
}

function formatAud(v) {
  if (v == null) return '—'
  const n = Number(v)
  return Number.isFinite(n)
    ? n.toLocaleString('en-AU', { style: 'currency', currency: 'AUD', maximumFractionDigits: 0 })
    : '—'
}

function formatDateTime(v) {
  if (!v) return '—'
  const d = new Date(v)
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString('en-AU', { dateStyle: 'medium', timeStyle: 'short' })
}

function StatCard({ icon: Icon, label, value, color = '#0A5169', bg = '#e8f2f6' }) {
  return (
    <div className="rounded-lg border border-gray-200 bg-white px-4 py-3 flex items-center gap-3 min-w-[140px]">
      <div
        className="w-9 h-9 rounded-full flex items-center justify-center shrink-0"
        style={{ backgroundColor: bg, color }}
      >
        <Icon className="w-5 h-5" />
      </div>
      <div className="min-w-0">
        <div className="text-xs text-gray-500 truncate">{label}</div>
        <div className="text-xl font-bold tabular-nums" style={{ color }}>{value ?? '—'}</div>
      </div>
    </div>
  )
}

export default function TtrRegistry() {
  const navigate = useNavigate()
  const [rows, setRows] = useState([])
  const [stats, setStats] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [filters, setFilters] = useState({
    status: '',
    q: '',
    overdue: false,
  })

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const params = new URLSearchParams()
      if (filters.status) params.set('status', filters.status)
      if (filters.overdue) params.set('overdue', 'true')
      const [listRes, statsRes] = await Promise.all([
        authFetch(getLocalApiUrl(`/api/ttr-registry${params.toString() ? `?${params}` : ''}`)),
        authFetch(getLocalApiUrl('/api/ttr-registry/stats')),
      ])
      const listJson = await listRes.json().catch(() => ({}))
      const statsJson = await statsRes.json().catch(() => ({}))
      if (!listJson.success) throw new Error(listJson.error || 'Failed to load TTRs')
      setRows(listJson.data || [])
      if (statsJson.success) setStats(statsJson.data)
    } catch (e) {
      setError(e.message || 'Failed to load')
    } finally {
      setLoading(false)
    }
  }, [filters.status, filters.overdue])

  useEffect(() => { load() }, [load])

  const filteredRows = useMemo(() => {
    const q = filters.q.trim().toLowerCase()
    if (!q) return rows
    return rows.filter((r) => {
      const memberLabel = stripTtrSeedDemoSuffix(r.member_name) ?? r.member_name ?? ''
      return (
        (r.id && r.id.toLowerCase().includes(q)) ||
        (r.member_id && String(r.member_id).toLowerCase().includes(q)) ||
        (memberLabel && memberLabel.toLowerCase().includes(q)) ||
        (r.payout_id && String(r.payout_id).toLowerCase().includes(q))
      )
    })
  }, [rows, filters.q])

  const totalCount = useMemo(() => {
    if (!stats) return rows.length
    return Object.values(stats.by_status || {}).reduce((a, b) => a + Number(b || 0), 0)
  }, [stats, rows])

  const exportCsv = () => {
    const headers = ['TTR ID', 'Status', 'Member', 'Member ID', 'Venue', 'Cash', 'Threshold', 'Regime', 'Form variant', 'Payout ID', 'Transaction time', 'Deadline', 'AUSTRAC ref', 'Filed at']
    const data = filteredRows.map((r) => [
      r.id, r.status, stripTtrSeedDemoSuffix(r.member_name) ?? r.member_name, r.member_id, r.venue_id,
      r.cash_amount, r.threshold_applied, r.regime_version, r.form_variant,
      r.payout_id, r.transaction_timestamp, r.deadline_at,
      r.austrac_reference || '', r.austrac_filed_at || '',
    ])
    const csv = [headers.join(','), ...data.map(row => row.map(c => `"${String(c ?? '').replace(/"/g, '""')}"`).join(','))].join('\n')
    const blob = new Blob([csv], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `ttr-registry-${new Date().toISOString().slice(0, 10)}.csv`
    a.click()
    URL.revokeObjectURL(url)
  }

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-2xl font-bold text-psg-teal flex items-center gap-2">
          <DocumentTextIcon className="w-6 h-6" />
          TTR Registry
        </h1>
        <p className="text-sm text-gray-500 mt-1">
          Threshold Transaction Reports (TTR-GS). Generated automatically from cash payouts at or above the AML/CTF Act threshold — $10,000 pre-reform, $5,000 post-reform.
        </p>
      </div>

      {/* Stats strip */}
      <div className="flex flex-wrap gap-3">
        <StatCard icon={DocumentTextIcon} label="Total TTRs"       value={totalCount} />
        <StatCard icon={ClockIcon}        label="Draft"            value={stats?.by_status?.draft || 0}                color="#92400e" bg="#fef3c7" />
        <StatCard icon={ShieldCheckIcon}  label="Ready"            value={stats?.by_status?.ready_for_submission || 0} color="#1e40af" bg="#dbeafe" />
        <StatCard icon={CheckCircleIcon}  label="Filed"            value={stats?.by_status?.filed || 0}                color="#166534" bg="#dcfce7" />
        <StatCard icon={ExclamationTriangleIcon} label="Overdue"   value={stats?.overdue_drafts || 0}                  color="#991b1b" bg="#fee2e2" />
        <StatCard icon={CheckCircleIcon}  label="Filed (7 days)"   value={stats?.filed_last_7_days || 0}               color="#166534" bg="#dcfce7" />
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 items-end">
        <div className="flex-1 min-w-[220px] max-w-sm relative">
          <MagnifyingGlassIcon className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            placeholder="Search by TTR ID, member, payout…"
            className="w-full pl-9 pr-3 py-2 rounded-lg border border-gray-200 text-sm outline-none focus:border-gray-300"
            value={filters.q}
            onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))}
          />
        </div>
        <div>
          <select
            className="px-3 py-2 rounded-lg border border-gray-200 text-sm bg-white"
            value={filters.status}
            onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))}
          >
            <option value="">All statuses</option>
            {Object.entries(STATUS_CONFIG).map(([k, v]) => (
              <option key={k} value={k}>{v.label}</option>
            ))}
          </select>
        </div>
        <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
          <input
            type="checkbox"
            checked={filters.overdue}
            onChange={(e) => setFilters((f) => ({ ...f, overdue: e.target.checked }))}
            className="rounded border-gray-300"
          />
          Only overdue
        </label>
        <Button type="button" variant="outline" size="sm" onClick={exportCsv} disabled={filteredRows.length === 0}>
          <ArrowDownTrayIcon className="w-4 h-4 mr-1" /> Export CSV
        </Button>
      </div>

      {/* Table */}
      <div className="rounded-lg border border-gray-200 bg-white overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left font-semibold text-gray-600">TTR</th>
              <th className="px-4 py-3 text-left font-semibold text-gray-600">Status</th>
              <th className="px-4 py-3 text-left font-semibold text-gray-600">Member</th>
              <th className="px-4 py-3 text-right font-semibold text-gray-600">Cash</th>
              <th className="px-4 py-3 text-right font-semibold text-gray-600">Threshold</th>
              <th className="px-4 py-3 text-left font-semibold text-gray-600">Regime</th>
              <th className="px-4 py-3 text-left font-semibold text-gray-600">Transaction</th>
              <th className="px-4 py-3 text-left font-semibold text-gray-600">Deadline</th>
              <th className="px-4 py-3 text-left font-semibold text-gray-600">Payout</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={10} className="px-4 py-12 text-center text-gray-500">Loading…</td></tr>
            )}
            {error && !loading && (
              <tr><td colSpan={10} className="px-4 py-12 text-center text-red-600">{error}</td></tr>
            )}
            {!loading && !error && filteredRows.length === 0 && (
              <tr><td colSpan={10} className="px-4 py-12 text-center text-gray-500">
                No TTR records. TTRs are generated automatically when a cash payout meets the reporting threshold.
              </td></tr>
            )}
            {!loading && !error && filteredRows.map((r) => (
              <tr
                key={r.id}
                className="border-t border-gray-100 hover:bg-gray-50/60 cursor-pointer"
                onClick={() => navigate(`/compliance-safe/ttr/${encodeURIComponent(r.id)}`)}
              >
                <td className="px-4 py-3 font-mono font-medium text-psg-teal">{r.id}</td>
                <td className="px-4 py-3"><StatusBadge status={r.status} /></td>
                <td className="px-4 py-3">
                  <div className="font-medium text-gray-800">{stripTtrSeedDemoSuffix(r.member_name) ?? r.member_name ?? '—'}</div>
                  <div className="text-xs font-mono text-gray-500">{r.member_id || '—'}</div>
                </td>
                <td className="px-4 py-3 text-right font-mono font-semibold text-gray-800">{formatAud(r.cash_amount)}</td>
                <td className="px-4 py-3 text-right font-mono text-gray-500">{formatAud(r.threshold_applied)}</td>
                <td className="px-4 py-3">
                  <Badge
                    bg={r.regime_version === '2006' ? '#f3e8ff' : '#e0f2fe'}
                    color={r.regime_version === '2006' ? '#6b21a8' : '#075985'}
                    title={r.form_variant === 'ttr_gs_reformed' ? 'Reformed TTR-GS form (from 1 Jul 2026)' : 'Current TTR-GS form'}
                  >
                    {r.regime_version}
                  </Badge>
                </td>
                <td className="px-4 py-3 text-sm text-gray-600">{formatDateTime(r.transaction_timestamp)}</td>
                <td className="px-4 py-3"><DeadlinePill deadlineAt={r.deadline_at} status={r.status} /></td>
                <td className="px-4 py-3 font-mono text-xs text-gray-500">{r.payout_id}</td>
                <td className="px-4 py-3 text-right">
                  <ChevronRightIcon className="w-4 h-4 text-gray-400" />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}
